import React, { FC } from "react";

import { DateTime } from "luxon";

import Box from "@mui/material/Box";
import styled from "@mui/material/styles/styled";
import {
  DateCalendar,
  DateCalendarProps,
} from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

import { roundedOutline } from "@readai/common/components/styles";
import Stack from "@readai/pulse/components/Stack";

export type DateCalendarDateTimeProps = DateCalendarProps<DateTime>;
export type PickersDayDateTimeProps = PickersDayProps<DateTime> & {
  backgroundColor?: React.CSSProperties["backgroundColor"];
};

export const Container = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.transparent.black[4]}`,
  borderRadius: "6px",
  width: "fit-content",
  position: "relative",
}));

type CalendarProps = DateCalendarDateTimeProps & { loading: boolean };

export const Calendar = styled<FC<CalendarProps>>(
  ({ loading: _l, ...props }) => <DateCalendar {...props} />,
)(({ loading, theme }) => ({
  width: "fit-content",
  "& .MuiDayCalendar-slideTransition": {
    minHeight: "254px",
  },
  "& .MuiDayCalendar-root": {
    width: "300px",
    opacity: loading ? 0.6 : 1,
  },
  "& .MuiPickersCalendarHeader-label": {
    fontWeight: 600,
  },
  "& .MuiPickersArrowSwitcher-button": {
    "&.Mui-disabled": { visibility: "hidden" },
    backgroundColor: "#EBEBF0",
    height: "24px",
    width: "24px",
    "&:first-of-type": { marginRight: "-4px" },
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
      color: "#323232",
    },
  },
  "& .MuiPickersCalendarHeader-root": {
    minHeight: "34px",
    maxHeight: "34px",
    borderBottom: `1px solid ${theme.palette.shades.neutral[50]}`,
    padding: 0,
    margin: 0,
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  "& .MuiDayCalendar-header > .MuiDayCalendar-weekDayLabel": {
    margin: "0 1px",
    height: "22px",
    width: "40px",
  },
}));

export const CalendarDay = styled<FC<PickersDayDateTimeProps>>(
  ({ backgroundColor: _bc, ...props }) => <PickersDay {...props} />,
)(({ backgroundColor, theme }) => ({
  fontSize: "15px",
  margin: "0 1px",
  border: "2px solid",
  position: "relative",
  height: "40px",
  width: "40px",

  "&.Mui-disabled:not(.Mui-selected)": {
    borderColor: theme.palette.shades.neutral[50],
    backgroundColor: theme.palette.shades.neutral[50],
    color: theme.palette.shades.neutral[500],
    borderRadius: 0,
  },
  "&:not(.Mui-disabled)": {
    backgroundColor,
    borderColor: "#FFF",
  },
  "&:hover": {
    ...roundedOutline({
      borderRadius: "50%",
      border: "1px solid #4C47CC",
    }),
    backgroundColor,
  },
  "&.MuiPickersDay-today": {
    borderWidth: "2px",
    "&::before": {
      content: "'•'",
      position: "absolute",
      bottom: -2,
      color: "inherit",
      fontSize: "10px",
    },
  },
  "&.Mui-selected": {
    backgroundColor: theme.palette.shades.primary[500],
    "&:focus": {
      backgroundColor: theme.palette.shades.primary[500],
    },
    opacity: "1 !important",
  },
}));

export const Legend = styled(Box)({
  background: "linear-gradient(90deg, #E8F9F5 0%, #A3E9D9 100%);",
  height: "8px",
  width: "40px",
  borderRadius: "8px",
});

export const LoadingContainer = styled(Stack)({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});
